import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { RoundState, SessionState } from '@ember/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface EndCardProps {
  session: SessionState;
  /**
   * Liste complete des rounds de la partie (avec reponses si revele).
   * Si vide, on affiche un fallback simple. Sinon on render le recap.
   */
  rounds: RoundState[];
  /**
   * True si le joueur local a fini AVANT que l'autre n'ait termine tous ses
   * rounds. On affiche alors une notice : "L'autre joueur termine encore..."
   */
  partnerStillPlaying?: boolean;
}

/**
 * Ecran de fin de partie avec recap complet :
 *   - Header "Partie terminee" + pseudos des joueurs
 *   - Liste des 10 rounds : pour chaque round, la question + les 2 reponses
 *     cote a cote (mobile : empilees, desktop : grid 2 colonnes)
 *   - Bouton "Retour a l'accueil" pour relancer
 *
 * Pour les rounds que l'autre n'a pas encore reveles (cas curseur local libre :
 * tu as fini en premier), on affiche "En attente de la reponse de [pseudo]".
 */
export function EndCard({ session, rounds, partnerStillPlaying = false }: EndCardProps) {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="space-y-6"
    >
      <div className="text-center space-y-3">
        <h1 className="text-3xl font-bold text-primary">Partie terminée</h1>
        <p className="text-muted-foreground">
          Vous avez partagé {rounds.length || 10} questions ensemble. Voici le récap.
        </p>
        <div className="flex justify-center gap-3 flex-wrap pt-2">
          {session.players.map((p) => (
            <span
              key={p.id}
              className="text-sm px-3 py-1 rounded-full bg-primary/15 text-primary font-medium"
            >
              {p.pseudo}
            </span>
          ))}
        </div>
      </div>

      {partnerStillPlaying && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="pt-6 text-center space-y-1">
            <p className="text-sm font-medium text-primary">
              Ton partenaire termine encore quelques questions.
            </p>
            <p className="text-xs text-muted-foreground">
              Le récap s'enrichira automatiquement quand ses réponses arriveront.
            </p>
          </CardContent>
        </Card>
      )}

      {rounds.length > 0 ? (
        <div className="space-y-4">
          {rounds.map((round) => (
            <RecapRound key={round.id} round={round} players={session.players} />
          ))}
        </div>
      ) : (
        <Card className="border-primary/40">
          <CardContent className="pt-6 text-center text-muted-foreground">
            Récap indisponible. Tu peux relancer une partie depuis l'accueil.
          </CardContent>
        </Card>
      )}

      <div className="flex justify-center pt-4">
        <Button size="lg" className="w-full sm:w-auto min-h-[44px]" onClick={() => navigate('/')}>
          Retour à l'accueil
        </Button>
      </div>
    </motion.div>
  );
}

interface RecapRoundProps {
  round: RoundState;
  players: SessionState['players'];
}

/**
 * Carte de recap pour un round :
 *   - Numero + intensite en header
 *   - Question
 *   - Pour chaque joueur de la session : sa reponse, ou "En attente" si non soumise
 */
function RecapRound({ round, players }: RecapRoundProps) {
  // Map playerId -> RoundAnswer pour ce round (contient text + guessByOthers eventuelles)
  const answerByPlayer = new Map(round.answers.map((a) => [a.playerId, a]));
  const isRevealed = round.status === 'REVEALED';

  return (
    <Card className="border-border">
      <CardContent className="pt-5 space-y-4">
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Question {round.order}
          </p>
          <p className="text-xs text-muted-foreground">Intensité {round.question.intensity}</p>
        </div>

        <p className="text-base font-medium leading-relaxed">{round.question.text}</p>

        <div className="grid sm:grid-cols-2 gap-3">
          {players.map((p) => {
            const answer = answerByPlayer.get(p.id);
            return (
              <div
                key={p.id}
                className="rounded-md border border-border bg-background/50 p-3 space-y-2"
              >
                <p className="text-xs uppercase tracking-wider text-primary font-medium">
                  {p.pseudo}
                </p>
                {answer ? (
                  <>
                    {answer.text && <p className="text-sm whitespace-pre-wrap">{answer.text}</p>}
                    {answer.media?.type === 'PHOTO' && (
                      <img
                        src={answer.media.url}
                        alt={`Photo de ${p.pseudo}`}
                        className="max-h-48 rounded-md mx-auto"
                      />
                    )}
                    {answer.media?.type === 'AUDIO' && (
                      <audio src={answer.media.url} controls className="w-full" />
                    )}
                    {answer.guessByOthers && answer.guessByOthers.length > 0 && (
                      <div className="pt-2 border-t border-border/60 space-y-1">
                        {answer.guessByOthers.map((g) => (
                          <p key={g.playerId} className="text-xs text-muted-foreground italic">
                            {g.pseudo} pensait : « {g.text} »
                          </p>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm italic text-muted-foreground">
                    {isRevealed ? 'Pas de réponse enregistrée' : 'En attente de sa réponse...'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
